/**
 * LD Exposure Dashboard — portfolio-wide LD accrual / waivers / cure plans.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Sparkles, Loader2, ArrowLeft, TrendingDown } from 'lucide-react';
import api from '@/lib/api';

const fmtINR = (v) => v == null ? '—' : (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Number(v).toLocaleString('en-IN')}`);

export default function ContractsLDExposure() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showCure, setShowCure] = useState(false);
  const [curing, setCuring] = useState(false);
  const [curePlan, setCurePlan] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get('/contracts/portfolio/ld-exposure'); setData(r.data); }
    catch { toast.error('Failed to load LD exposure'); } finally { setLoading(false); }
  })(); }, []);

  const rows = data?.trackers || [];
  const totals = data?.totals || {};
  const counts = data?.counts || {};

  const cureFor = async (tracker) => {
    setSelected(tracker); setShowCure(true); setCurePlan(null); setCuring(true);
    try { const r = await api.post(`/contracts/ld-trackers/${tracker.id}/cure-plan`); setCurePlan(r.data); toast.success('Cure plan ready'); }
    catch { toast.error('Cure plan failed'); } finally { setCuring(false); }
  };

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-ld-exposure">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="ld-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">LD Exposure Dashboard</h1>
            <p className="text-sm text-muted-foreground mt-1">Real-time LD accrual · Monte-Carlo forecast · waivers ledger · Cognara cure plans</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="ld-kpis">
          {[
            { label: 'Trackers', value: counts.total || 0, sub: `${counts.near_cap || 0} near cap`, color: '#94A3B8' },
            { label: 'Gross Exposure', value: fmtINR(totals.exposure), sub: 'Cumulative', color: '#EF4444' },
            { label: 'Waived', value: fmtINR(totals.waived), sub: 'Ledger', color: '#22C55E' },
            { label: 'Net Exposure', value: fmtINR(totals.net_exposure), sub: 'After waivers', color: '#F97316' },
            { label: 'Cap Breached', value: counts.breached || 0, sub: 'Trackers', color: '#DC2626' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
              <p className="text-[10px] text-muted-foreground">{kpi.sub}</p>
            </CardContent></Card>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">LD Trackers</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="ld-table">
                <thead className="text-muted-foreground uppercase text-[10px]">
                  <tr>{['Milestone', 'Contract', 'Project', 'Delay', 'Rate/day', 'Current Exp.', 'Forecast Exp.', 'Cap Used', 'Waived', 'Action'].map(h => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {rows.map(t => (
                    <tr key={t.id} className="border-t border-border hover:bg-white/[0.02]" data-testid={`ld-row-${t.id}`}>
                      <td className="px-2 py-2 text-foreground">{t.milestone}<div className="text-[10px] text-muted-foreground">{t.ld_type}</div></td>
                      <td className="px-2 py-2 text-foreground/80">{t.contract_number || '—'}</td>
                      <td className="px-2 py-2 text-muted-foreground">{t.project}</td>
                      <td className="px-2 py-2 font-semibold" style={{ color: t.delay_days > 0 ? '#EF4444' : '#22C55E' }}>{t.delay_days}d</td>
                      <td className="px-2 py-2 text-foreground/80">{fmtINR(t.daily_rate)}</td>
                      <td className="px-2 py-2 text-orange-400">{fmtINR(t.current_exposure)}</td>
                      <td className="px-2 py-2 text-red-400">{fmtINR(t.forecast_exposure)}</td>
                      <td className="px-2 py-2 min-w-[120px]">
                        {t.cap_percent != null ? (<div className="flex items-center gap-2">
                          <Progress value={t.cap_percent} className="h-1.5 w-16" />
                          <span className="text-[10px]" style={{ color: t.cap_percent >= 90 ? '#EF4444' : t.cap_percent >= 70 ? '#F59E0B' : '#22C55E' }}>{t.cap_percent}%</span>
                        </div>) : <span className="text-muted-foreground text-[10px]">No cap</span>}
                      </td>
                      <td className="px-2 py-2 text-emerald-400">{fmtINR(t.waived_amount)} ({t.waivers_count})</td>
                      <td className="px-2 py-2"><Button size="sm" variant="ghost" onClick={() => cureFor(t)} className="h-6 px-2 text-purple-400 hover:text-purple-300" data-testid={`ld-cure-${t.id}`}><Sparkles className="w-3 h-3 mr-1" />Cure Plan</Button></td>
                    </tr>
                  ))}
                  {rows.length === 0 && <tr><td colSpan={10} className="text-center py-6 text-muted-foreground">No LD trackers</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showCure} onOpenChange={setShowCure}>
          <DialogContent className="max-w-2xl bg-card border-border text-foreground" data-testid="ld-cure-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Cognara Cure Plan — {selected?.milestone}</DialogTitle>
              <DialogDescription>Actions, acceleration options, and waiver ask.</DialogDescription>
            </DialogHeader>
            {curing ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" /><span className="text-sm text-muted-foreground">Drafting cure plan...</span></div>
            ) : curePlan ? (
              <div className="space-y-4 text-sm" data-testid="ld-cure-plan">
                <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-foreground">{curePlan.title}</h4>
                    <Badge style={{ background: '#22C55E22', color: '#4ADE80' }}>{curePlan.success_probability_pct}% success</Badge>
                  </div>
                  <p className="text-foreground/80">{curePlan.objective}</p>
                </div>
                {curePlan.actions?.length > 0 && <div>
                  <h5 className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Actions</h5>
                  <div className="space-y-1">{curePlan.actions.map((a, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs p-2 rounded" style={{ background: 'var(--muted)' }}>
                      <span className="text-muted-foreground w-6">{i + 1}.</span>
                      <span className="flex-1 text-foreground">{a.step}</span>
                      <span className="text-muted-foreground">{a.owner}</span>
                      <Badge variant="outline" className="text-[9px]">{a.dueDays}d</Badge>
                    </div>
                  ))}</div>
                </div>}
                {curePlan.acceleration_options?.length > 0 && <div>
                  <h5 className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Acceleration Options</h5>
                  <div className="grid grid-cols-1 gap-1">{curePlan.acceleration_options.map((o, i) => (
                    <div key={i} className="p-2 rounded text-xs" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                      <div className="font-semibold text-foreground">{o.option}</div>
                      <div className="text-muted-foreground">Cost: {o.cost} · Days saved: {o.days_saved}</div>
                    </div>
                  ))}</div>
                </div>}
                {curePlan.waiver_ask && <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid #F59E0B44' }}>
                  <h5 className="text-xs uppercase tracking-widest text-amber-400 mb-1">Waiver Ask</h5>
                  <p className="text-foreground font-semibold">{curePlan.waiver_ask.amount}</p>
                  <p className="text-foreground/80 text-xs mt-1">{curePlan.waiver_ask.justification}</p>
                </div>}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
